import { pipe } from "fp";

import React from "react";
import styled from "styled-components";

import { Company } from "~/Company";
import * as Data from "~/Data";
import * as Driver from "~/Driver";
import { English } from "~/English";
import { ID } from "~/ID";
import * as Layout from "~/Layout";
import * as Loading from "~/Loading";
import * as Log from "~/Log";
import { NotApplicable } from "~/NotApplicable";
import { Remote } from "~/Remote";
import * as Table from "~/Table";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

export const Component = (route: Log.Route) => (
  <Layout.Section.Component>
    <TableWithoutTopBorder>
      <Head.Component />
      {Log.Data.useActiveLogWhen(route, {
        unavailable: () => <Table.Wide.Loading.Component colSpan={8} />,
        available: (log) => <Form.Component {...log} />,
      })}
    </TableWithoutTopBorder>
  </Layout.Section.Component>
);

const TableWithoutTopBorder = styled(Table.Component)`
  margin: -1px;
`;

namespace Head {
  export const Component = () => (
    <Table.Head.Component>
      <Table.Row.Component hover={false}>
        <Table.Header.Component>
          <Title.Component>Time Zone</Title.Component>
        </Table.Header.Component>
        <Table.Header.Component>
          <Title.Component>Co-Driver</Title.Component>
        </Table.Header.Component>
        <Table.Header.Component>
          <Title.Component>Vehicles</Title.Component>
        </Table.Header.Component>
        <Table.Header.Component>
          <Title.Component>Trailers</Title.Component>
        </Table.Header.Component>
        <Table.Header.Component>
          <Title.Component>Distance</Title.Component>
        </Table.Header.Component>
        <Table.Header.Component>
          <Title.Component>Documents</Title.Component>
        </Table.Header.Component>
        <Table.Header.Component>
          <Title.Component>Carrier DOT</Title.Component>
        </Table.Header.Component>
        <Table.Header.Component>
          <Title.Component>Home Office</Title.Component>
        </Table.Header.Component>
      </Table.Row.Component>
    </Table.Head.Component>
  );
}

namespace Form {
  export const Component: React.Fn<Log.Log> = ({
    driver,
    coDriver,
    assets,
    distance,
    documents,
    events,
  }) => {
    const driverZone = Driver.useFromReference({
      ...driver,
      missing: () => undefined,
      found: ({ time }) => time?.zone,
    });

    const companyDOT = pipe(
      Company.use().company,
      Remote.map(({ dot }) => dot),
      Remote.toUndefined
    );

    const distinctAssetsFromLogs = events.reduce<readonly string[]>(
      (assets, event) => {
        const logAsset = event.sources.current;
        if (logAsset.asset?.id) {
          const existingAsset = assets.find((id) => id === logAsset.asset?.id);
          if (!existingAsset) {
            return [...assets, logAsset.asset.id];
          }
        }
        return assets;
      },
      []
    );

    return (
      <Table.Body.Component>
        <Table.Row.Component hover={false}>
          <Column.Component>
            <Layout.Space>
              {driverZone ? (
                <Time.Zone zone={driverZone} />
              ) : (
                <Loading.Component />
              )}
            </Layout.Space>
          </Column.Component>
          <Column.Component>{coDriver ?? <NotApplicable />}</Column.Component>
          <Column.Component>
            <Asset.Component assets={assets?.vehicles} />
          </Column.Component>
          <Column.Component>
            <Asset.Component assets={assets?.trailers} />
          </Column.Component>
          <Column.Component>{`${distance.toFixed(1)} mi`}</Column.Component>
          <Column.Component>{documents ?? <NotApplicable />}</Column.Component>
          <Column.Component>
            <div>
              {distinctAssetsFromLogs.length > 0
                ? distinctAssetsFromLogs.map((assetID, index) => [
                    index > 0 && ", ",
                    <AssetDOTNumber
                      key={assetID}
                      asset={{ id: assetID }}
                      companyDOT={companyDOT}
                    />,
                  ])
                : companyDOT}
            </div>
          </Column.Component>
          <Column.Component>
            <HomeOffice.Component {...driver} />
          </Column.Component>
        </Table.Row.Component>
      </Table.Body.Component>
    );
  };
}

export const AssetDOTNumber = ({
  asset,
  companyDOT,
}: {
  readonly asset: ID;
  readonly companyDOT?: string;
}) => {
  const assetDOT = Remote.toUndefined(Log.Assets.DOT.use({ asset }));
  return (
    <span data-testid="asset-dot-number">
      {assetDOT ? assetDOT.dotNumber : companyDOT}
    </span>
  );
};

namespace Column {
  export const Component = styled(Table.Column.Component)`
    width: calc(100% / 7);
  `;
}

namespace Asset {
  export const Component: React.Fn<{
    readonly assets?: readonly string[];
  }> = ({ assets }) => (
    <>
      {pipe(assets ?? [], ([first, ...rest]) =>
        first === undefined || first === "" ? (
          <NotApplicable />
        ) : (
          English.list(first, ...rest)
        )
      )}
    </>
  );
}

namespace HomeOffice {
  export const Component: React.Fn<Data.Reference.Reference> = (driver) =>
    Driver.useFromReference({
      ...driver,
      missing: () => <NotApplicable />,
      found: (driver) =>
        driver.homeOffice ? (
          <Company.Location {...driver.homeOffice} />
        ) : (
          <NotApplicable />
        ),
    });
}

namespace Title {
  export const Component: Component = ({ children }) => (
    <Typography.H4.Component>{children}</Typography.H4.Component>
  );
}
